import { ButtonGroup } from '@mui/material';
import React, { useCallback, useState } from 'react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadButton from '@components/UploadButton/UploadButton';
import { LoadingButton } from '@mui/lab';
import { useToast } from 'hooks';
import { useBoolean } from 'usehooks-ts';
import { ApprovalDialog } from '@components/Forms';
import { ToastSeverity } from 'types/toast';
import { getErrorMessage } from '@utils/flows';

interface IExportImportButtonsProps<T> {
	onExport: () => Promise<void>;
	onImport: (data: T) => Promise<any>;
	toastLabelPrefix: string;
	question?: string;
	title?: string;
	onImportCompleted?: () => void;
}

const ExportImportButtons = <T extends unknown>({
	onImport,
	onExport,
	onImportCompleted,
	question,
	toastLabelPrefix,
	title
}: IExportImportButtonsProps<T>) => {
	const { addToast } = useToast();
	const [uploadedValue, setUploadedValue] = useState<T | null>(null);
	const { value: approvalOpen, toggle: toggleApproval } = useBoolean(false);
	const { value: exportLoading, toggle: toggleExport } = useBoolean(false);
	const { value: importLoading, toggle: toggleImport } = useBoolean(false);
	const handleExport = useCallback(async () => {
		toggleExport();
		await onExport();
		toggleExport();
	}, [onExport, toggleExport]);
	const handleImportConfirm = useCallback(async () => {
		toggleApproval();
		setUploadedValue(null);
		try {
			await onImport(uploadedValue!);
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr(`${toastLabelPrefix}.success`)
			});
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: getErrorMessage(error, intl.tr(`${toastLabelPrefix}.error`)),
				error
			});
		} finally {
			toggleImport();
			onImportCompleted?.();
		}
	}, [
		uploadedValue,
		onImportCompleted,
		toggleImport,
		toastLabelPrefix,
		onImport,
		toggleApproval,
		addToast
	]);
	const handleImport = useCallback(
		async (data: string | ArrayBuffer) => {
			toggleImport();
			const out = JSON.parse(data as string) as T;
			toggleApproval();
			setUploadedValue(out);
		},
		[toggleImport, setUploadedValue, toggleApproval]
	);
	return (
		<ButtonGroup disableElevation variant="contained">
			<LoadingButton
				onClick={handleExport}
				loading={exportLoading}
				aria-label={intl.tr('button.export.item', { item: 'flow' })}
				title={intl.tr('button.export.item', { item: 'flow' })}
				data-testid="export-button"
			>
				{!exportLoading && <FileDownloadIcon color="primary" />}
			</LoadingButton>
			{approvalOpen && title && (
				<ApprovalDialog
					approveText={intl.tr('button.continue')}
					question={question}
					title={title}
					variant="outlined"
					onApprove={handleImportConfirm}
					cancelText={intl.tr('button.cancel')}
					onCancel={() => {
						toggleApproval();
						toggleImport();
						setUploadedValue(null);
					}}
				/>
			)}
			<UploadButton
				inputName="import-input"
				accept="application/json"
				size="large"
				loading={importLoading}
				onFileUpload={handleImport}
				text={intl.tr('button.import.item', { item: 'flow' })}
				iconOnly
				readFullFile
			/>
		</ButtonGroup>
	);
};

ExportImportButtons.defaultProps = {
	title: undefined,
	question: undefined,
	onImportCompleted: undefined
};

export default ExportImportButtons;
